/**
 * SUB-3.11 · FR58. What a superadmin is told before and after trying to delete
 * an account that still has sub-accounts.
 *
 * Pure, and here rather than in the page, for the same reason as
 * `subAccountDetach.ts`: the two sentences a superadmin reads are the whole
 * user-facing surface of this rule, and they should be testable without
 * rendering a table.
 *
 * ⚠️ NOTHING HERE DECIDES WHETHER A DELETE IS ALLOWED, and the confirm button
 * stays enabled even when this module knows there are children. FR58 is enforced
 * by `trg_refuse_tenant_delete_with_sub_accounts` in joules-web
 * `20260813g_sub_3_11_lifecycle_hierarchy.sql`, on a BEFORE DELETE trigger with
 * no service_role exemption, precisely so that it binds the path this app takes
 * (`DELETE /tenants/{id}` -> PostgREST as service_role) rather than only
 * `hard_delete_tenant`. A client-side gate would be a second definition of the
 * rule, computed from a tenant list that can be seconds out of date, and it
 * would be the only one the operator ever read. What this module does instead is
 * tell them what is about to happen, and then show them what the database
 * actually said.
 */

export interface DeletableTenantRow {
  tenant_id: string;
  company_name: string;
  parent_tenant_id?: string | null;
  is_archived?: boolean | null;
}

/**
 * The children of one account, archived ones INCLUDED.
 *
 * Archived children are the trap in this story. `sub_account_usage()` excludes
 * them, because FR67 is about the allowance, but an archived sub-account still
 * carries `parent_tenant_id` and still blocks the delete: archiving is none of
 * FR58's three ways forward. A list that hid them would show "no sub-accounts"
 * next to a delete that is refused for having one.
 */
export function selectChildren<T extends DeletableTenantRow>(
  tenants: readonly T[],
  parentTenantId: string,
): T[] {
  if (!parentTenantId) return [];
  return tenants
    .filter((t) => t.parent_tenant_id === parentTenantId)
    .slice()
    .sort((a, b) =>
      a.company_name.localeCompare(b.company_name, undefined, {
        sensitivity: "base",
      }),
    );
}

/** Up to `max` names, then "and N more", so thirteen clients stay readable. */
function nameList(children: readonly DeletableTenantRow[], max = 5): string {
  const shown = children
    .slice(0, max)
    .map((c) => (c.is_archived ? `${c.company_name} (archived)` : c.company_name));
  const rest = children.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ");
}

/**
 * The confirmation. Two different dialogs, because they are two different
 * facts and merging them into one hedged sentence helps nobody.
 *
 * With no children it is the ordinary irreversible-delete warning. With
 * children it leads with the refusal, names them, and gives the three ways
 * forward in the order the database's own message gives them, so the operator
 * does not meet two differently worded versions of one rule.
 */
export function deleteConfirmation(
  tenantName: string,
  children: readonly DeletableTenantRow[],
): { title: string; description: string; confirmLabel: string } {
  if (children.length === 0) {
    return {
      title: "Delete tenant?",
      description:
        `This permanently deletes ${tenantName} and cannot be undone. If this ` +
        `account is ever made a parent of client accounts, deleting it will be ` +
        `refused until those are released, transferred or deleted.`,
      confirmLabel: "Delete",
    };
  }

  const n = children.length;
  const archived = children.filter((c) => c.is_archived).length;

  return {
    title: `${tenantName} has client accounts`,
    description:
      `Deleting ${tenantName} will be refused: it is the parent of ${n} ` +
      `client ${n === 1 ? "account" : "accounts"} (${nameList(children)}). ` +
      `Release ${n === 1 ? "it" : "them"}, transfer ${n === 1 ? "it" : "them"} ` +
      `to another parent, or delete ${n === 1 ? "it" : "them"} first. ` +
      (archived > 0
        ? `${archived} of ${n === 1 ? "them is" : "them are"} archived, and ` +
          `archiving is not one of those three: an archived client account still ` +
          `names ${tenantName} as its parent. `
        : "") +
      `Archiving ${tenantName} instead leaves every client account running and ` +
      `only withdraws its Agency Admins' access while it stays archived.`,
    confirmLabel: "Try anyway",
  };
}

/**
 * The sentence after the fact.
 *
 * ⚠️ THE SERVER'S WORDING IS PREFERRED OVER ANYTHING THIS FILE COULD COMPOSE.
 * The refusal names the sub-accounts as they are at the moment of the attempt,
 * which the tenant list in this app may not be. `_rpc_http_error` in
 * joules-admin-backend maps SQLSTATE 23001 to a 409 carrying that text intact,
 * and it used to be flattened into "Failed to delete tenant".
 */
export function describeDeleteError(error: unknown, tenantName: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (error instanceof Error && error.message) return error.message;
  return `${tenantName} could not be deleted.`;
}
